import { Box, Stack } from '@mui/material';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { Card } from '@/components/layout/Card/Card';
import { FloatingMenuContainer } from '@/components/layout/FloatingMenuContainer';
import { Markdown } from '@/components/Markdown/Markdown';
import { DocumentLoaderProvider } from '@/components/progress/DocumentLoader';
import { BdgDeathOrderSwitch } from '@/modules/beneficiaryDesignations/BdgDeathOrderSwitch';
import { SingleEntityDispositiveDiagram } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/diagram/SingleEntityDispositiveDiagram';
import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';
import { DocumentSearchBar } from '@/modules/pdf/DocumentSearch/DocumentSearchBar';
import { PDFViewerLazy } from '@/modules/pdf/PDFViewer';
import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { AnnotatedPDFViewerProvider } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.provider';
import { Annotations } from '@/modules/pdf/PDFViewer/Annotations';
import { computeAutoZoomLevel } from '@/modules/pdf/PDFViewer/computeAutoZoomLevel';
import { DocumentChromeProps } from '@/modules/pdf/PDFViewer/DocumentChrome';
import { PDFViewerSlots } from '@/modules/pdf/PDFViewer/PDFViewer.types';
import { PDFZoomControls } from '@/modules/pdf/PDFViewer/PDFZoomControls';
import { convertGraphQLFilePages } from '@/modules/pdf/utils/filePageUtils';
import { useIsDocumentWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsDocumentWorkflowTrackerInProgress';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';
import { WorkflowTrackerKind } from '@/types/schema';

import { useHouseholdDocumentsViewerContext } from './context/HouseholdDocumentsViewer.context';
import { DocumentRenderError } from './DocumentRenderError';
import { DocumentSelector } from './DocumentSelector/DocumentSelector';
import { DocumentSummarySwitch } from './DocumentSummarySwitch';
import {
  DocumentEmptyStateSlot,
  DocumentViewerTabEmptyState,
} from './DocumentViewerTabEmptyState';
import {
  FileParagraphPicker,
  ParagraphPickerOptions,
} from './FileParagraphPicker';
import {
  FindCitation,
  ParagraphFind,
  ParagraphFindOverlay,
} from './ParagraphFind';
import { SummarySelector } from './SummarySelector/SummarySelector';

type InsetCardChromeProps = DocumentChromeProps;

function ParagraphFindWithExtraction(
  props: ComponentProps<typeof ParagraphFind>
) {
  const processing = useIsDocumentWorkflowTrackerInProgress(
    props.documentId,
    WorkflowTrackerKind.DocumentSummary
  );
  return <ParagraphFind {...props} processing={processing} />;
}

function HouseholdDocumentsFloatingMenuContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <FloatingMenuContainer offset={{ top: 16 }} sx={{ mx: 2 }}>
      {children}
    </FloatingMenuContainer>
  );
}

/**
 * Positions controls (zoom, document selector) at the top-right of the viewer.
 * Uses absolute positioning to ensure consistent layout across document/summary/diagram tabs,
 * preventing controls from affecting content flow or stacking incorrectly.
 */
function TopRightControls({
  children,
  rightOffset = 0,
}: {
  children: React.ReactNode;
  rightOffset?: number;
}) {
  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      sx={{
        position: 'absolute',
        top: 0,
        right: rightOffset,
        zIndex: zIndexes.MENU,
      }}
    >
      {children}
    </Stack>
  );
}

/**
 * Opens the active document's detail page in a new tab. Reads the active
 * document and household from the viewer context. Surfaced in the top-right
 * controls only when the host opts in via `shouldShowOpenInNewTab` (e.g. the
 * presentation source-document drawer).
 */
function OpenDocumentInNewTabButton() {
  const { activeDocument, householdId } = useHouseholdDocumentsViewerContext();
  const documentId = activeDocument?.id;

  const openInNewTab = () => {
    if (!documentId || !householdId) {
      return;
    }
    const path = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
      { householdId, documentId }
    );
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      startIcon={LinkExternal01Icon}
      onClick={openInNewTab}
      disabled={!documentId}
    >
      Open in new tab
    </Button>
  );
}

export function InsetCardChrome({
  children,
  shouldCenterContent,
  scrollContainerRef,
  sx = {},
}: InsetCardChromeProps) {
  return (
    <Card
      inputRef={scrollContainerRef}
      data-name="InsetCardChrome"
      variant="transparent"
      sx={{
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'auto',
        px: 2,
        pt: 3,
        borderRadius: 0,
        ...sx,
      }}
    >
      {/*
        we have this extra container because if the relative parent of the annotations has padding,
        then the absolute positioning of the annotation overlays won't correctly consider the
        padding and will be offset
      */}
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: shouldCenterContent ? 'center' : 'start',
        }}
      >
        {children}
      </Box>
    </Card>
  );
}

function HouseholdDocumentViewerWithAnnotations({
  initialPrimaryClientId,
  showDiagram = false,
  autoZoom = false,
  hideDocumentRightBorder = false,
  documentChromeSx,
  topRightControlsRightOffset = 0,
  shouldShowOpenInNewTab = false,
  hideDocumentSummarySwitch,
  hideDocumentSearch,
  enableTextSearch = false,
  paragraphPicker,
  findCitation,
  loadingSlots,
}: {
  initialPrimaryClientId?: string;
  showDiagram?: boolean;
  autoZoom?: boolean;
  hideDocumentRightBorder?: boolean;
  documentChromeSx?: InsetCardChromeProps['sx'];
  topRightControlsRightOffset?: number;
  shouldShowOpenInNewTab?: boolean;
  hideDocumentSummarySwitch?: boolean;
  hideDocumentSearch?: boolean;
  enableTextSearch?: boolean;
  paragraphPicker?: ParagraphPickerOptions;
  findCitation?: FindCitation;
  loadingSlots?: HouseholdDocumentsViewerProps['loadingSlots'];
}) {
  const [zoomLevel, setZoomLevel] = useState(1.0);

  const {
    activeDocument,
    activeDocumentId,
    documentViewerActiveTab,
    error,
    loading,
    householdId,
    activeSummaryId,
  } = useHouseholdDocumentsViewerContext();
  const { annotations, activeAnnotationIndex } = useAnnotatedPDFViewerContext();
  const Find =
    enableTextSearch && activeDocument
      ? ParagraphFindWithExtraction
      : ParagraphFind;

  // prefer the PDF file, and fall back to the potentially non-pdf file
  const file = activeDocument?.pdfFile ?? activeDocument?.file;
  const fileId = file?.id;

  const rawPages = activeDocument?.pdfFile?.pages ?? activeDocument?.file.pages;
  const filePages = useMemo(
    () => convertGraphQLFilePages(rawPages),
    [rawPages]
  );
  const paragraphOverlayConfig =
    paragraphPicker && activeDocument && file
      ? {
          fileID: file.id,
          documentId: activeDocument.id,
          documentName: activeDocument.name,
          picker: paragraphPicker,
        }
      : undefined;
  const paragraphOverlayConfigRef = useRef(paragraphOverlayConfig);
  paragraphOverlayConfigRef.current = paragraphOverlayConfig;
  const ParagraphOverlay = useMemo(() => {
    return function ParagraphOverlay(
      props: ComponentProps<typeof Annotations>
    ) {
      const config = paragraphOverlayConfigRef.current;
      // Keep Find in the same tree position when picking starts or ends, so
      // its search state and pending scroll requests survive the change.
      return (
        <>
          <ParagraphFindOverlay {...props} hideCitations={!!config} />
          {config && (
            <FileParagraphPicker
              key={`${config.fileID}:${config.picker.action ?? 'add'}`}
              {...props}
              documentId={config.documentId}
              documentName={config.documentName}
              picker={config.picker}
            />
          )}
        </>
      );
    };
  }, []);

  // Track whether the user has manually adjusted zoom since the last auto-zoom,
  // so we don't fight manual adjustments.
  const userOverrodeZoomRef = useRef(false);

  // Reset the user-override flag whenever the evidence target changes (new
  // citation or different suggestion).  This effect MUST be declared before the
  // auto-zoom effect so it runs first in the same commit phase.
  useEffect(() => {
    userOverrodeZoomRef.current = false;
  }, [annotations, activeAnnotationIndex]);

  // Auto-zoom when annotations appear and autoZoom is enabled
  useEffect(() => {
    if (!autoZoom || !annotations?.length || userOverrodeZoomRef.current) {
      return;
    }

    const newZoom = computeAutoZoomLevel(
      annotations,
      activeAnnotationIndex,
      filePages
    );
    setZoomLevel(newZoom);
  }, [autoZoom, annotations, activeAnnotationIndex, filePages]);

  const handleZoomLevelChange = useCallback((newZoom: number) => {
    userOverrodeZoomRef.current = true;
    setZoomLevel(newZoom);
  }, []);

  const summaries = activeDocument?.summaries ?? [];
  const activeSummary =
    summaries.find((s) => s.id === activeSummaryId) ?? summaries[0];

  return (
    // using `overflow="hidden"` to mark this as a "scrollable" ancestor to the floating menu
    // so the floating menu will be positioned relative to this element
    <Box
      overflow="hidden"
      height="100%"
      sx={
        documentViewerActiveTab === 'document' && !hideDocumentRightBorder
          ? { borderRight: 1, borderColor: COLORS.GRAY[200] }
          : undefined
      }
    >
      {documentViewerActiveTab === 'document' && (
        <>
          <HouseholdDocumentsFloatingMenuContainer>
            {!hideDocumentSummarySwitch && (
              <DocumentSummarySwitch showDiagram={showDiagram} />
            )}
            <TopRightControls rightOffset={topRightControlsRightOffset}>
              {shouldShowOpenInNewTab && <OpenDocumentInNewTabButton />}
              <PDFZoomControls
                onZoomLevelChange={handleZoomLevelChange}
                externalZoomLevel={zoomLevel}
              />
              <DocumentSelector />
            </TopRightControls>
          </HouseholdDocumentsFloatingMenuContainer>
          <DocumentLoaderProvider key={activeDocumentId}>
            <Find
              key={`${activeDocument?.id}:${fileId}`}
              fileID={fileId ?? ''}
              documentId={activeDocument?.id ?? ''}
              enabled={enableTextSearch && !!fileId}
              loadParagraphs={
                (enableTextSearch || !!paragraphPicker) && !!fileId
              }
              findCitation={findCitation}
            >
              <PDFViewerLazy
                // using `key` to force remounting the whole PDFViewer to reset the state
                // when the document changes
                key={fileId ?? 'empty'}
                loading={loading || !householdId}
                error={!!error}
                documentId={activeDocument?.id}
                source={fileId ? { kind: 'file', fileId } : null}
                annotations={annotations}
                activeAnnotationIdx={activeAnnotationIndex}
                zoomLevel={zoomLevel}
                slots={{
                  ...loadingSlots,
                  DocumentEmptyState: DocumentEmptyStateSlot,
                  DocumentChrome: InsetCardChrome,
                  DocumentTopLeftControl: undefined,
                  DocumentTopRightControl: undefined,
                  DocumentFooter: undefined,
                  DocumentRenderError: DocumentRenderError,
                  ...(paragraphOverlayConfig || enableTextSearch
                    ? { DocumentOverlay: ParagraphOverlay }
                    : {}),
                }}
                documentChromeProps={{ sx: documentChromeSx }}
                filePages={filePages}
              />
            </Find>
          </DocumentLoaderProvider>
          {!hideDocumentSearch && !paragraphPicker && (
            <FloatingMenuContainer
              offset={{ bottom: 3 * 8 }}
              sx={{ ml: 2, mr: 3 }}
            >
              <DocumentSearchBar isDocumentLoading={loading} />
            </FloatingMenuContainer>
          )}
        </>
      )}
      {documentViewerActiveTab === 'summary' && (
        <>
          <HouseholdDocumentsFloatingMenuContainer>
            <DocumentSummarySwitch showDiagram={showDiagram} />
            <TopRightControls rightOffset={topRightControlsRightOffset}>
              {shouldShowOpenInNewTab && <OpenDocumentInNewTabButton />}
              <SummarySelector />
            </TopRightControls>
          </HouseholdDocumentsFloatingMenuContainer>
          {activeSummary ? (
            <Box
              height="100%"
              sx={{
                height: '100%',
                position: 'relative',
                borderRadius: 0,
                borderRight: 1,
                borderColor: COLORS.GRAY[200],
                overflowY: 'auto',
                pl: 2,
              }}
            >
              <Card
                variant="transparent"
                sx={{
                  maxWidth: 708,
                  margin: '0 auto',
                  pt: 8,
                }}
              >
                <Markdown markdown={activeSummary.summary} />
              </Card>
            </Box>
          ) : (
            <DocumentViewerTabEmptyState activeTab="summary" />
          )}
        </>
      )}
      {documentViewerActiveTab === 'diagram' && (
        <>
          <HouseholdDocumentsFloatingMenuContainer>
            <DocumentSummarySwitch showDiagram={showDiagram} />
            <BdgDeathOrderSwitch />
          </HouseholdDocumentsFloatingMenuContainer>
          <Box
            height="100%"
            sx={{
              height: '100%',
              position: 'relative',
              borderRight: 1,
              borderColor: COLORS.GRAY[200],
              overflow: 'hidden',
            }}
          >
            <SingleEntityDispositiveDiagram
              initialPrimaryClientId={initialPrimaryClientId}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export function HouseholdDocumentsViewerInner({
  overrideAnnotations,
  overrideActiveAnnotationIndex,
  overrideSearchQuery,
  onUpdateSearchQuery,
  overrideSearchLoading,
  initialPrimaryClientId,
  showDiagram,
  autoZoom,
  hideDocumentRightBorder,
  documentChromeSx,
  topRightControlsRightOffset,
  shouldShowOpenInNewTab,
  hideDocumentSummarySwitch,
  hideDocumentSearch,
  enableTextSearch,
  paragraphPicker,
  findCitation,
  loadingSlots,
}: Pick<
  HouseholdDocumentsViewerProps,
  | 'overrideAnnotations'
  | 'overrideActiveAnnotationIndex'
  | 'overrideSearchQuery'
  | 'onUpdateSearchQuery'
  | 'overrideSearchLoading'
  | 'initialPrimaryClientId'
  | 'showDiagram'
  | 'autoZoom'
  | 'hideDocumentRightBorder'
  | 'documentChromeSx'
  | 'topRightControlsRightOffset'
  | 'shouldShowOpenInNewTab'
  | 'hideDocumentSummarySwitch'
  | 'hideDocumentSearch'
  | 'enableTextSearch'
  | 'paragraphPicker'
  | 'findCitation'
  | 'loadingSlots'
>) {
  const { activeDocumentId } = useHouseholdDocumentsViewerContext();

  return (
    <AnnotatedPDFViewerProvider
      documentId={activeDocumentId}
      overrideAnnotations={overrideAnnotations}
      overrideActiveAnnotationIndex={overrideActiveAnnotationIndex}
      overrideSearchQuery={overrideSearchQuery}
      onUpdateSearchQuery={onUpdateSearchQuery}
      overrideSearchLoading={overrideSearchLoading}
    >
      <HouseholdDocumentViewerWithAnnotations
        initialPrimaryClientId={initialPrimaryClientId}
        showDiagram={showDiagram}
        autoZoom={autoZoom}
        hideDocumentRightBorder={hideDocumentRightBorder}
        documentChromeSx={documentChromeSx}
        topRightControlsRightOffset={topRightControlsRightOffset}
        shouldShowOpenInNewTab={shouldShowOpenInNewTab}
        hideDocumentSummarySwitch={hideDocumentSummarySwitch}
        hideDocumentSearch={hideDocumentSearch}
        enableTextSearch={enableTextSearch}
        paragraphPicker={paragraphPicker}
        findCitation={findCitation}
        loadingSlots={loadingSlots}
      />
    </AnnotatedPDFViewerProvider>
  );
}

export interface HouseholdDocumentsViewerProps {
  loadingSlots?: Pick<PDFViewerSlots, 'LoadingExternal' | 'DocumentLoading'>;
  paragraphPicker?: ParagraphPickerOptions;
  /** Connects Find to the citation that the host has in focus. */
  findCitation?: FindCitation;
  hideDocumentSummarySwitch?: boolean;
  hideDocumentSearch?: boolean;
  enableTextSearch?: boolean;
  overrideAnnotations?: DocumentSearchResult[];
  overrideActiveAnnotationIndex?: number;
  overrideSearchQuery?: string;
  onUpdateSearchQuery?: () => void;
  overrideSearchLoading?: boolean;
  /** For disposition diagrams: initial primary client first-death id */
  initialPrimaryClientId?: string;
  showDiagram?: boolean;
  /** When true, automatically zoom the PDF to make the focused annotation readable. */
  autoZoom?: boolean;
  /** When true, omit the viewer border because the host renders its own divider. */
  hideDocumentRightBorder?: boolean;
  /** Optional styles for the scrollable document chrome. */
  documentChromeSx?: InsetCardChromeProps['sx'];
  /** Optional right inset for top-right document controls. Defaults to flush right. */
  topRightControlsRightOffset?: number;
  /**
   * When true, show an "Open in new tab" control that opens the active
   * document's detail page in a new tab. Opt-in (defaults off) since the
   * viewer is embedded in flows where leaving for the document page is
   * undesirable; the presentation source-document drawer enables it.
   */
  shouldShowOpenInNewTab?: boolean;
}

export function HouseholdDocumentsViewer(props: HouseholdDocumentsViewerProps) {
  return (
    <HouseholdDocumentsViewerInner
      overrideAnnotations={props.overrideAnnotations}
      overrideActiveAnnotationIndex={props.overrideActiveAnnotationIndex}
      overrideSearchQuery={props.overrideSearchQuery}
      onUpdateSearchQuery={props.onUpdateSearchQuery}
      overrideSearchLoading={props.overrideSearchLoading}
      initialPrimaryClientId={props.initialPrimaryClientId}
      showDiagram={props.showDiagram}
      autoZoom={props.autoZoom}
      hideDocumentRightBorder={props.hideDocumentRightBorder}
      documentChromeSx={props.documentChromeSx}
      topRightControlsRightOffset={props.topRightControlsRightOffset}
      shouldShowOpenInNewTab={props.shouldShowOpenInNewTab}
      hideDocumentSummarySwitch={props.hideDocumentSummarySwitch}
      hideDocumentSearch={props.hideDocumentSearch}
      enableTextSearch={props.enableTextSearch}
      paragraphPicker={props.paragraphPicker}
      findCitation={props.findCitation}
      loadingSlots={props.loadingSlots}
    />
  );
}
